import { useLayoutEffect, useMemo, useState } from 'react';
import { useQueries, useQueryClient, UseQueryResult } from 'react-query';
import {
  consultarDashboardPainel,
  DadosPainel,
  RecursoPainel,
  RECURSOS_PAINEL,
} from '../dashboardPainel';
import { FiltrosDashboard } from './useDashboard';

type DadosRecurso<K extends RecursoPainel> = DadosPainel[K] extends {
  rows: infer R;
}
  ? K extends 'evolucao'
    ? DadosPainel[K]
    : R
  : DadosPainel[K];
export interface EstadoRecurso<T> {
  data: T | undefined;
  error: Error | undefined;
  isError: boolean;
  isLoading: boolean;
  isPreviousData: boolean;
  refetch: () => Promise<void>;
}
export type EstadoPainel = {
  [K in RecursoPainel]: EstadoRecurso<DadosRecurso<K>>;
};
type Grupo = Partial<
  Record<RecursoPainel, { data?: unknown; error?: Error; httpStatus?: number }>
>;

// Heavy actions settle separately. This immutable partition is not a cache
// or an authorization state; mutable state stays in this hook and P2 QueryClient.
const GRUPOS: RecursoPainel[][] = [
  ['indicadores'],
  ['evolucao'],
  ['pendencias'],
  ['contas', 'movimentacoes', 'ultimasConciliacoes', 'contasCadastro'],
];
function erroLegivel(error: unknown): Error {
  const response = (
    error as {
      response?: { data?: { msg?: string; error?: { message?: string } } };
    }
  )?.response;
  const message = response?.data?.msg ?? response?.data?.error?.message;
  return message
    ? new Error(message)
    : error instanceof Error
    ? error
    : new Error('Não foi possível carregar este recurso. Tente novamente.');
}
async function carregar(
  filtros: FiltrosDashboard,
  ids: RecursoPainel[],
): Promise<Grupo> {
  try {
    const resposta = await consultarDashboardPainel(filtros, ids);
    return Object.fromEntries(
      ids.map(id => {
        const item = resposta.resources[id];
        if (!item)
          throw new Error(
            'O Bancário recebeu uma resposta inválida. Tente novamente.',
          );
        if (item.error) return [id, { error: new Error(item.error.message) }];
        const { data } = item;
        return [
          id,
          {
            data:
              id === 'contas' ||
              id === 'movimentacoes' ||
              id === 'ultimasConciliacoes' ||
              id === 'contasCadastro'
                ? (data as { rows: unknown[] }).rows
                : data,
          },
        ];
      }),
    );
  } catch (error) {
    const failure = erroLegivel(error);
    const httpStatus = (error as { response?: { status?: number } })?.response
      ?.status;
    return Object.fromEntries(
      ids.map(id => [id, { error: failure, httpStatus }]),
    );
  }
}

export function useDashboardPainel(filtros: FiltrosDashboard): EstadoPainel {
  const queryClient = useQueryClient();
  const identity = JSON.stringify(filtros);
  const execution = useMemo(
    () => ({
      queryClient,
      identity,
      active: true,
      versions: [0, 0, 0, 0],
      retries: new Map<RecursoPainel, Promise<void>>(),
      overrides: {} as Grupo,
    }),
    [queryClient, identity],
  );
  const [, render] = useState(0);
  useLayoutEffect(() => {
    execution.active = true;
    return () => {
      execution.active = false;
    };
  }, [execution]);

  const keys = GRUPOS.map(ids => ['dashboard', 'painel', filtros, ids]);
  const queries = useQueries(
    GRUPOS.map((ids, index) => ({
      queryKey: keys[index],
      queryFn: () => {
        execution.versions[index] += 1;
        // Superseded retries must not keep a newer failure loading or block
        // its recovery. Their eventual result still fails the version guard.
        ids.forEach(id => {
          execution.retries.delete(id);
          delete execution.overrides[id];
        });
        return carregar(filtros, ids);
      },
      onSuccess: () => {
        const settled = queryClient.getQueryState<Grupo>(keys[index]);
        if (!execution.active || !settled || settled.isFetching) return;
        // The group has settled. Persist newer selective results only now:
        // setQueryData during a fetch would mark its unresolved siblings settled.
        const unauthorized = ids.some(
          id => settled.data?.[id]?.httpStatus === 401,
        );
        if (unauthorized) execution.versions[index] += 1;
        const overrides: Grupo = {};
        ids.forEach(id => {
          if (!unauthorized && execution.overrides[id])
            overrides[id] = execution.overrides[id];
          if (unauthorized) execution.retries.delete(id);
          delete execution.overrides[id];
        });
        // Clear first: React Query also calls onSuccess for setQueryData.
        if (Object.keys(overrides).length) {
          queryClient.setQueryData<Grupo>(keys[index], current => ({
            ...current,
            ...overrides,
          }));
        }
      },
      retry: 0,
      staleTime: 30000,
      keepPreviousData: true,
      refetchOnWindowFocus: 'always' as const,
    })),
  ) as UseQueryResult<Grupo>[];

  function refetch(id: RecursoPainel, index: number): Promise<void> {
    const inFlight = execution.retries.get(id);
    if (inFlight) return inFlight;
    if (!execution.active) return Promise.resolve();
    const version = execution.versions[index];
    const promise = carregar(filtros, [id])
      .then(result => {
        // A new group load, filter, scope or unmount invalidates this retry.
        // Do not recreate an outgoing cache cleared by the P2 boundary.
        if (
          execution.active &&
          execution.versions[index] === version &&
          queryClient.getQueryState(keys[index])
        ) {
          if (queryClient.getQueryState(keys[index])?.isFetching) {
            // This resource is fresh, while siblings still use their truthful
            // pending/previous observer state until the whole group completes.
            execution.overrides[id] = result[id];
          } else {
            queryClient.setQueryData<Grupo>(keys[index], current => ({
              ...current,
              ...result,
            }));
          }
        }
      })
      .finally(() => {
        if (execution.retries.get(id) === promise) execution.retries.delete(id);
        if (execution.active) render(value => value + 1);
      });
    execution.retries.set(id, promise);
    render(value => value + 1);
    return promise;
  }

  return Object.fromEntries(
    RECURSOS_PAINEL.map(id => {
      const index = GRUPOS.findIndex(ids => ids.includes(id));
      const query = queries[index];
      const override = execution.overrides[id];
      const resource = override ?? query.data?.[id];
      const retrying = execution.retries.has(id);
      return [
        id,
        {
          data: resource?.data,
          error: resource?.error,
          isError: !!resource?.error,
          isLoading:
            (!override && query.isLoading) || (retrying && !resource?.data),
          isPreviousData:
            (!override && query.isPreviousData) ||
            (retrying && !!resource?.data),
          refetch: () => refetch(id, index),
        },
      ];
    }),
  ) as EstadoPainel;
}
